import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, Checkbox, FormControlLabel, Paper, Typography } from '@mui/material';

import { BackButton, PageTitle } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';
import DatePicker from '../common/DatePicker';

import {
  combineFullDateFromInputs,
  getInputFieldValuesFromDate,
  validateMonthRange,
} from '../SelectDocumentCreationDate/dateUtils';
import { dateDiff, formatDate } from '../../../../shared/utils';
import { VALID_DATE, NOT_VALID_DATE, IN_PAST_DATE } from '../../../../shared/constants';

import { StyledHintContainer, StyledVerticalContent } from './SelectTreatmentDate.styled';

const SelectTreatmentDate = ({
  backButtonLink,
  nextButtonLink,
  pageTitleId,
  formDescription,
  setTreatmentDate,
  treatmentDate,
  documentCreationDate,
  checkLabelText,
  showWarningText,
  contractAcceptedAt,
}) => {
  const history = useHistory();
  const intl = useIntl();

  const treatmentDateInputFieldValues = getInputFieldValuesFromDate(treatmentDate);
  const documentCreationDateInputFieldValues = getInputFieldValuesFromDate(documentCreationDate);
  const [date, setDate] = useState(treatmentDateInputFieldValues);

  const { day, month, year } = date || {};
  const setDay = (newDay) => setDate({ day: newDay, month, year });
  const setMonth = (newMonth) => setDate({ day, month: newMonth, year });
  const setYear = (newYear) => setDate({ day, month, year: newYear });

  const newTreatmentDate = combineFullDateFromInputs(day, month, year);

  const [sameDocumentDateChecker, setSameDocumentDateChecker] = useState(true);

  const [dateStatus, setDateStatus] = useState(VALID_DATE); // ['valid','notValid','inPast']

  useEffect(() => {
    const sameDates = newTreatmentDate === documentCreationDate;

    if (sameDocumentDateChecker !== sameDates) {
      setSameDocumentDateChecker(sameDates);
    }

    const newTreatmentDateObject = new Date(newTreatmentDate);
    const currentDate = new Date();

    const isValidDate = !Number.isNaN(Number(newTreatmentDateObject));
    if (isValidDate) {
      setDateStatus(VALID_DATE);
    }

    const notInTheFuture = isValidDate ? newTreatmentDateObject < currentDate : false;
    if (notInTheFuture && validateMonthRange(newTreatmentDate)) {
      setTreatmentDate(newTreatmentDate);
    } else {
      setTreatmentDate('');
    }
  }, [newTreatmentDate, documentCreationDate, sameDocumentDateChecker, setTreatmentDate]);

  const handleCheckboxChange = (event) => {
    if (sameDocumentDateChecker) {
      setDate({ day: '', month: '', year: '' });
    } else {
      setDate(documentCreationDateInputFieldValues);
    }
    setSameDocumentDateChecker(event.target.checked);
  };

  const handleButtonClick = () => {
    if (!treatmentDate || !validateMonthRange(treatmentDate)) {
      setDateStatus(NOT_VALID_DATE);
    } else if (
      treatmentDate &&
      validateMonthRange(treatmentDate) &&
      dateDiff('year', new Date(treatmentDate)) >= 5
    ) {
      setDateStatus(IN_PAST_DATE);
    } else {
      history.push(nextButtonLink);
    }
  };

  const disableNextButton =
    dateStatus !== VALID_DATE || !day || !month || !year || year.length !== 4;

  const treatmentDateBeforeContractAcceptedDate =
    new Date(contractAcceptedAt) > new Date(newTreatmentDate);

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </PageTitle>

      <StyledVerticalContent>
        <Paper>
          <StyledVerticalContent>
            <DatePicker
              label={intl.formatMessage({ id: formDescription })}
              day={day}
              month={month}
              year={year}
              setDay={setDay}
              setMonth={setMonth}
              setYear={setYear}
            />

            <FormControlLabel
              control={
                <Checkbox
                  id="same-as-document-date-checkbox"
                  name="same-as-document-date-checkbox"
                  checked={sameDocumentDateChecker}
                  onChange={handleCheckboxChange}
                  slotProps={{
                    input: {
                      'aria-label': intl.formatMessage({
                        id: checkLabelText,
                      }),
                      'aria-checked': sameDocumentDateChecker ? 'true' : 'false',
                    },
                  }}
                />
              }
              label={
                <Typography variant="p">
                  <FormattedMessage id={checkLabelText} />
                </Typography>
              }
            />

            {dateStatus === NOT_VALID_DATE && (
              <Typography variant="p" color="error" data-testid="error-date">
                <FormattedMessage id="new_claim.invalid.date" />
              </Typography>
            )}
            {dateStatus === IN_PAST_DATE && (
              <Typography variant="p" color="error" data-testid="error-date">
                <FormattedMessage id="new_claim.invalid.date_past" />
              </Typography>
            )}
          </StyledVerticalContent>
        </Paper>

        <Typography variant="p" color="professional_blue">
          <FormattedMessage id="new_claim.select_treatment_creation_date.helper_text" />
        </Typography>

        {showWarningText && treatmentDateBeforeContractAcceptedDate && !disableNextButton && (
          <StyledHintContainer>
            <Typography variant="p" color="professional_blue">
              <FormattedMessage
                id="new_claim.select_treatment_creation_date.warning_text"
                values={{
                  contractAcceptedAt: formatDate(contractAcceptedAt),
                }}
              />
            </Typography>
          </StyledHintContainer>
        )}
      </StyledVerticalContent>

      <ButtonContainer>
        <Button data-testid="next_button" onClick={handleButtonClick} disabled={disableNextButton}>
          <Typography variant="buttons">
            <FormattedMessage id="common.button.next" />
          </Typography>
        </Button>
      </ButtonContainer>
    </>
  );
};

export default SelectTreatmentDate;
